import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  memo
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { fileService } from '../../services/api';
import Button from '../../components/shared/Button';
import SidebarLayout from '../../components/SidebarLayout';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  Download,
  FileText,
  AlertCircle,
  Trash2,
  Columns,
  SlidersHorizontal
} from 'lucide-react';
import styles from './ActivityLogsPage.module.css';

const DENSE_MODE_STORAGE_KEY = 'activityLogs_dense_mode';
const COLUMN_VISIBILITY_STORAGE_KEY = 'activityLogs_column_visibility';
const COLUMN_WIDTHS_STORAGE_KEY = 'activityLogs_column_widths';
const MIN_VISIBLE_COLUMNS = 1;

const cx = (...classes) => classes.filter(Boolean).join(' ');

const getStoredPreference = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch (error) {
    console.warn('Failed to parse stored preference', error);
    return fallback;
  }
};

const VirtualizedOuter = forwardRef(function VirtualizedOuter(props, ref) {
  return (
    <div
      {...props}
      ref={ref}
      role="rowgroup"
      className={cx(styles.virtualScrollContainer, props.className)}
    />
  );
});

const VirtualRow = memo(({ data, index, style }) => {
  const {
    logs,
    visibleColumns,
    denseMode,
    isSelected,
    toggleSelect,
    handleDownload,
    formatDate,
    gridTemplateColumns
  } = data;

  const log = logs[index];
  if (!log) return null;

  const rowClassName = cx(
    styles.tableRow,
    denseMode && styles.denseRow,
    log.status === 0 ? styles.tableRowDeleted : '',
    index % 2 === 0 ? styles.evenRow : styles.oddRow
  );

  const successRate = Number(log.success_rate ?? 0);
  const rateBadgeClass =
    successRate >= 90 ? styles.high : successRate >= 70 ? styles.medium : styles.low;

  const rowStyle = {
    ...style,
    gridTemplateColumns
  };

  return (
    <div
      role="row"
      aria-selected={isSelected(log.id)}
      className={rowClassName}
      style={rowStyle}
    >
      {visibleColumns.map((column) => {
        let content = null;
        switch (column.id) {
          case 'select':
            content = (
              <input
                type="checkbox"
                checked={isSelected(log.id)}
                onChange={() => toggleSelect(log.id)}
                aria-label={`Select ${log.file_name}`}
              />
            );
            break;
          case 'srNo':
            content = <span>{index + 1}</span>;
            break;
          case 'file_name':
            content = (
              <span className={styles.fileNameText} title={log.file_name}>
                {log.file_name}
              </span>
            );
            break;
          case 'total_processed':
            content = log.total_processed;
            break;
          case 'total_accepted':
            content = log.total_accepted;
            break;
          case 'total_failed':
            content = log.total_failed;
            break;
          case 'success_rate':
            content = (
              <span className={cx(styles.rateBadge, rateBadgeClass)}>
                {successRate.toFixed(2)}%
              </span>
            );
            break;
          case 'status':
            content = log.status === 0 ? 'Deleted' : 'Active';
            break;
          case 'date':
            content = formatDate(log.date);
            break;
          case 'original_file':
            content =
              log.originalFileUrl && log.status !== 0 ? (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => handleDownload(log.originalFileUrl, log.file_name, 'original')}
                  className={styles.downloadButton}
                >
                  <Download size={14} />
                  Download
                </Button>
              ) : (
                <span
                  className={styles.expiredButton}
                  title={
                    log.status === 0 ? 'Not available for deleted logs' : 'File expired or deleted'
                  }
                >
                  <Download size={14} />
                  {log.status === 0 ? 'Deleted' : 'Expired'}
                </span>
              );
            break;
          case 'rejected_file':
            content =
              log.rejectedFileUrl && log.status !== 0 ? (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() =>
                    handleDownload(log.rejectedFileUrl, `${log.file_name}_rejected`, 'rejected')
                  }
                  className={styles.downloadButton}
                >
                  <Download size={14} />
                  Download
                </Button>
              ) : log.total_failed > 0 ? (
                <span
                  className={styles.expiredButton}
                  title={
                    log.status === 0
                      ? 'Not available for deleted logs'
                      : 'Rejected file expired or deleted'
                  }
                >
                  <Download size={14} />
                  {log.status === 0 ? 'Deleted' : 'Expired'}
                </span>
              ) : (
                <span className={styles.noFileButton} title="No rejected rows - all data was successfully processed">
                  <FileText size={14} />
                  No File
                </span>
              );
            break;
          default:
            content = log[column.id] ?? '--';
        }

        return (
          <div key={column.id} role="cell" className={cx(styles.cell, styles[column.id])}>
            {content}
          </div>
        );
      })}
    </div>
  );
});

const ActivityLogsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();

  const [activityLogs, setActivityLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [showColumnPanel, setShowColumnPanel] = useState(false);

  const columnPanelRef = useRef(null);
  const columnButtonRef = useRef(null);

  const columns = useMemo(
    () => [
      {
        id: 'select',
        label: '',
        width: 44,
        minWidth: 40,
        disableVisibilityToggle: true,
        resizable: false
      },
      { id: 'srNo', label: 'Sr.No', width: 60, minWidth: 56 },
      { id: 'file_name', label: 'File Name', width: 220, minWidth: 160 },
      { id: 'total_processed', label: 'Total Processed', width: 120, minWidth: 100 },
      { id: 'total_accepted', label: 'Total Accepted', width: 120, minWidth: 100 },
      { id: 'total_failed', label: 'Total Failed', width: 120, minWidth: 100 },
      { id: 'success_rate', label: 'Success Rate %', width: 140, minWidth: 120 },
      { id: 'status', label: 'Status', width: 110, minWidth: 96 },
      { id: 'date', label: 'Date', width: 180, minWidth: 150 },
      { id: 'original_file', label: 'Original File', width: 150, minWidth: 130 },
      { id: 'rejected_file', label: 'Rejected File', width: 150, minWidth: 130 }
    ],
    []
  );

  const columnMap = useMemo(() => {
    const map = {};
    columns.forEach((column) => {
      map[column.id] = column;
    });
    return map;
  }, [columns]);

  const [denseMode, setDenseMode] = useState(() =>
    getStoredPreference(DENSE_MODE_STORAGE_KEY, true)
  );
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const fallback = columns.reduce((acc, column) => {
      acc[column.id] = true;
      return acc;
    }, {});
    return getStoredPreference(COLUMN_VISIBILITY_STORAGE_KEY, fallback);
  });
  const [columnWidths, setColumnWidths] = useState(() => {
    const stored = getStoredPreference(COLUMN_WIDTHS_STORAGE_KEY, {});
    return columns.reduce((acc, column) => {
      acc[column.id] = stored[column.id] || column.width;
      return acc;
    }, {});
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(DENSE_MODE_STORAGE_KEY, JSON.stringify(denseMode));
  }, [denseMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths));
  }, [columnWidths]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showColumnPanel &&
        columnPanelRef.current &&
        !columnPanelRef.current.contains(event.target) &&
        columnButtonRef.current &&
        !columnButtonRef.current.contains(event.target)
      ) {
        setShowColumnPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnPanel]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => columnVisibility[column.id] !== false),
    [columns, columnVisibility]
  );

  useEffect(() => {
    if (visibleColumns.length === 0) {
      setColumnVisibility((prev) => ({
        ...prev,
        file_name: true
      }));
    }
  }, [visibleColumns.length]);

  const gridTemplateColumns = useMemo(() => {
    if (visibleColumns.length === 0) return '';
    return visibleColumns
      .map((column) => `${Math.max(column.minWidth, columnWidths[column.id] || column.width)}px`)
      .join(' ');
  }, [visibleColumns, columnWidths]);

  const totalTableWidth = useMemo(() => {
    if (visibleColumns.length === 0) return 0;
    return visibleColumns.reduce(
      (sum, column) =>
        sum + Math.max(column.minWidth, columnWidths[column.id] || column.width),
      0
    );
  }, [visibleColumns, columnWidths]);

  const virtualizationKey = useMemo(
    () =>
      visibleColumns
        .map((column) => `${column.id}:${columnWidths[column.id] || column.width}`)
        .join('|'),
    [visibleColumns, columnWidths]
  );

  const handleDenseModeToggle = () => setDenseMode((prev) => !prev);

  const handleColumnVisibilityChange = (columnId) => {
    const column = columnMap[columnId];
    if (!column || column.disableVisibilityToggle) return;

    const visibleToggleableColumns = columns.filter(
      (col) => !col.disableVisibilityToggle && columnVisibility[col.id] !== false
    );

    if (visibleToggleableColumns.length <= MIN_VISIBLE_COLUMNS && columnVisibility[columnId]) {
      showInfo('At least one data column must remain visible');
      return;
    }

    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !(prev[columnId] !== false)
    }));
  };

  const handleResetColumns = () => {
    const defaultVisibility = columns.reduce((acc, column) => {
      acc[column.id] = true;
      return acc;
    }, {});
    const defaultWidths = columns.reduce((acc, column) => {
      acc[column.id] = column.width;
      return acc;
    }, {});
    setColumnVisibility(defaultVisibility);
    setColumnWidths(defaultWidths);
  };

  const resizingInfoRef = useRef(null);
  const handleColumnResizeStart = (event, columnId) => {
    event.preventDefault();
    event.stopPropagation();

    const column = columnMap[columnId];
    if (!column) return;

    const startX = event.clientX;
    const startWidth = columnWidths[columnId] || column.width;

    resizingInfoRef.current = { columnId, startX, startWidth };

    const handleMouseMove = (moveEvent) => {
      if (!resizingInfoRef.current) return;
      const delta = moveEvent.clientX - resizingInfoRef.current.startX;
      const nextWidth = Math.max(column.minWidth, resizingInfoRef.current.startWidth + delta);
      setColumnWidths((prev) => ({
        ...prev,
        [columnId]: nextWidth
      }));
    };

    const handleMouseUp = () => {
      resizingInfoRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleColumnResizeKeyDown = (event, columnId) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const column = columnMap[columnId];
    if (!column || column.resizable === false) return;

    event.preventDefault();
    const delta = event.key === 'ArrowLeft' ? -10 : 10;
    setColumnWidths((prev) => {
      const current = prev[columnId] || column.width;
      const nextWidth = Math.max(column.minWidth, current + delta);
      return {
        ...prev,
        [columnId]: nextWidth
      };
    });
  };

  const rowHeight = denseMode ? 36 : 54;
  const virtualizedHeight = denseMode ? '68vh' : '58vh';

  const handleDownload = useCallback(
    async (url, fileName, type) => {
      if (!url) {
        showError('File is no longer available (expired or deleted)');
        return;
      }

      try {
        showInfo(`Downloading ${type} file...`);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName || 'default'}_${type}.xlsx`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showSuccess(`${type} file downloaded successfully!`);
      } catch (downloadError) {
        console.error(`Error downloading ${type} file:`, downloadError);
        showError(`Failed to download ${type} file: ${downloadError.message}`);
      }
    },
    [showError, showInfo, showSuccess]
  );

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }, []);

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === activityLogs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(activityLogs.map((log) => log.id)));
    }
  }, [activityLogs, selectedIds]);

  const listData = useMemo(() => {
    const template =
      gridTemplateColumns ||
      visibleColumns
        .map((column) => `${Math.max(column.minWidth, columnWidths[column.id] || column.width)}px`)
        .join(' ');

    return {
      logs: activityLogs,
      visibleColumns,
      denseMode,
      isSelected,
      toggleSelect,
      handleDownload,
      formatDate,
      gridTemplateColumns: template
    };
  }, [
    activityLogs,
    visibleColumns,
    denseMode,
    isSelected,
    toggleSelect,
    handleDownload,
    formatDate,
    gridTemplateColumns,
    columnWidths
  ]);

  const itemKey = useCallback(
    (index, data) => data.logs[index]?.id ?? `row-${index}`,
    []
  );

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchActivityLogs();
  }, [token]);

  const fetchActivityLogs = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Debug: Check if function exists
      // console.log('powerBiService:', fileService);
      // console.log('getActivityLogs function:', fileService.getActivityLogs);

      if (typeof fileService.getActivityLogs !== 'function') {
        throw new Error('getActivityLogs function is not available');
      }

      const logs = await fileService.getActivityLogs();
      setActivityLogs(logs);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Removed handleBackToDashboard - sidebar navigation handles this

  const purgeSelected = async () => {
    try {
      setShowConfirm(false);
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      // Fire deletes sequentially to avoid overwhelming server; could be parallel if needed
      let successCount = 0;
      setSelectedIds(new Set());
      showInfo('Deleting logs...');
      for (const id of ids) {
        try {
          await fileService.deleteUpload(id);
          successCount++;
        } catch (e) {
          console.error('Failed to delete upload', id, e);
        }
      }
      if (successCount > 0) {
        showSuccess(`Deleted ${successCount} log${successCount > 1 ? 's' : ''}`);
      } else {
        showError('No logs were deleted');
      }
      // Refresh logs
      await fetchActivityLogs();
    } catch (e) {
      showError(e.message || 'Failed to purge selected logs');
    }
  };

  if (!token) {
    return null; // Will redirect to login
  }

  return (
    <SidebarLayout>
      <div className={styles.activityLogsPage}>
        <div className={styles.container}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              {/* Removed back button - sidebar handles navigation */}
              <div className={styles.titleSection}>
                <h1 className={styles.title}>
                  <FileText size={24} />
                  📊 Activity Logs
                </h1>
                <p className={styles.subtitle}>
                  Track your file uploads and processing history
                </p>
              </div>
            </div>
          </div>

        </div>
        {/* Content */}
        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Loading your activity logs...</p>
            </div>
          ) : error ? (
            <div className={styles.errorState}>
              <AlertCircle size={48} className={styles.errorIcon} />
              <h3>Error Loading Activity Logs</h3>
              <p>{error}</p>
              <Button variant="primary" onClick={fetchActivityLogs}>
                Try Again
              </Button>
            </div>
          ) : activityLogs.length === 0 ? (
            <div className={styles.emptyState}>
              <FileText size={64} className={styles.emptyIcon} />
              <h3>No uploads found yet</h3>
              <p>You haven't uploaded any files yet. Start by uploading your first Excel file!</p>
              <Button variant="primary" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <div className={cx(styles.tableHeader, denseMode && styles.denseHeader)}>
                <div className={styles.tableHeaderTop}>
                  <div>
                    <h3>
                      Upload History{' '}
                      {activityLogs.length > 0
                        ? `(${activityLogs.length} ${activityLogs.length === 1 ? 'file' : 'files'})`
                        : '(No files found)'}
                    </h3>
                    <p className={styles.tableHint}>
                      Dense layout {denseMode ? 'enabled' : 'disabled'} · Showing virtually rendered
                      rows for optimal performance
                    </p>
                  </div>
                  <div className={styles.tableControls}>
                    <label className={styles.denseToggle}>
                      <input
                        type="checkbox"
                        checked={denseMode}
                        onChange={handleDenseModeToggle}
                        aria-label="Toggle dense mode"
                      />
                      <span>Dense Mode</span>
                    </label>
                    <button
                      ref={columnButtonRef}
                      className={styles.columnToggleButton}
                      onClick={() => setShowColumnPanel((prev) => !prev)}
                      aria-expanded={showColumnPanel}
                      aria-controls="column-visibility-panel"
                    >
                      <Columns size={16} />
                      Columns
                    </button>
                    {showColumnPanel && (
                      <div
                        id="column-visibility-panel"
                        ref={columnPanelRef}
                        className={styles.columnPanel}
                        role="dialog"
                        aria-label="Column visibility settings"
                      >
                        <div className={styles.columnPanelHeader}>
                          <span>Visible Columns</span>
                          <button className={styles.resetButton} onClick={handleResetColumns}>
                            Reset
                          </button>
                        </div>
                        <div className={styles.columnPanelList}>
                          {columns
                            .filter((column) => !column.disableVisibilityToggle)
                            .map((column) => (
                              <label key={column.id} className={styles.columnPanelItem}>
                                <input
                                  type="checkbox"
                                  checked={columnVisibility[column.id] !== false}
                                  onChange={() => handleColumnVisibilityChange(column.id)}
                                />
                                {column.label}
                              </label>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.tableHeaderBottom}>
                  <label className={styles.selectAllGroup}>
                    <input
                      className={styles.selectAllCheckbox}
                      type="checkbox"
                      onChange={toggleSelectAll}
                      checked={selectedIds.size === activityLogs.length && activityLogs.length > 0}
                      aria-label="Select all logs"
                    />
                    Select All
                  </label>
                  <button
                    className={styles.purgeButton}
                    disabled={selectedIds.size === 0}
                    onClick={() => setShowConfirm(true)}
                    title={selectedIds.size === 0 ? 'Select logs to enable' : 'Purge selected logs'}
                  >
                    <Trash2 size={14} style={{ marginRight: 6 }} />
                    Purge Selected ({selectedIds.size})
                  </button>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <div className={styles.horizontalScroll}>
                  <div
                    className={cx(styles.tableSurface, denseMode && styles.denseTable)}
                    style={{ minWidth: Math.max(totalTableWidth, 1) }}
                    role="table"
                    aria-label="Upload history"
                  >
                    <div
                      className={styles.tableHeaderRow}
                      role="row"
                      style={{
                        gridTemplateColumns:
                          gridTemplateColumns ||
                          `repeat(${visibleColumns.length || 1}, minmax(80px, 1fr))`
                      }}
                    >
                      {visibleColumns.map((column) => (
                        <div
                          key={column.id}
                          role="columnheader"
                          className={styles.columnHeader}
                          style={{ minWidth: columnWidths[column.id] || column.width }}
                        >
                          <span>{column.label}</span>
                          {column.resizable !== false && (
                            <span
                              role="separator"
                              tabIndex={0}
                              aria-label={`Resize ${column.label || 'column'}`}
                              className={styles.columnResizeHandle}
                              onMouseDown={(event) => handleColumnResizeStart(event, column.id)}
                              onKeyDown={(event) => handleColumnResizeKeyDown(event, column.id)}
                            >
                              <SlidersHorizontal size={12} />
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    <div
                      className={styles.virtualArea}
                      style={{ height: virtualizedHeight }}
                      aria-live="polite"
                    >
                      <AutoSizer disableWidth>
                        {({ height }) => (
                          <List
                            key={virtualizationKey}
                            height={height}
                            width={Math.max(totalTableWidth, 1)}
                            itemCount={activityLogs.length}
                            itemSize={rowHeight}
                            itemData={listData}
                            itemKey={itemKey}
                            outerElementType={VirtualizedOuter}
                            className={styles.virtualList}
                            overscanCount={16}
                          >
                            {VirtualRow}
                          </List>
                        )}
                      </AutoSizer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>

      {showConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Confirm Deletion</h3>
            <p className={styles.modalText}>Are you sure you want to delete the selected logs?</p>
            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
              <Button variant="primary" onClick={purgeSelected}>Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
};

export default ActivityLogsPage;
